import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ContextService,
  LabelsService,
  Language,
  provideAiguillage,
  provideLocaleId,
  provideTypology,
  withApimInterceptors,
} from '@bcef/ia-shared';
import { DataLayer, provideDataLayer } from '@difa/ia-data-layer';
import { provideTypologyFileUrl } from '@difa/ia-typology-files';
import { provideJsonConvert } from '@difa/ia-utils';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';

import { environment } from '@environments/environment';
import { enrollmentEffects } from '@features/enrollment/store';
import { enrollmentFeature } from '@features/enrollment/store/reducers/enrollment.reducers';
import { globalEffects } from '@features/global/store';
import { globalFeature } from '@features/global/store/reducers/global.reducers';
import { reconciliationEffects } from '@features/reconciliation/store';
import { reconciliationFeature } from '@features/reconciliation/store/reducers/reconciliation.reducers';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideStore(
      {
        router: routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
        },
      },
    ),
    environment.providers,
    provideRouterStore({
      stateKey: 'router',
    }),
    provideAiguillage(),
    provideTranslateService({
      defaultLanguage: Language.FR,
      fallbackLang: Language.FR,
      loader: provideTranslateLoader(LabelsService),
    }),
    provideLocaleId(),
    provideTypology(),
    provideTypologyFileUrl(() => {
      const context = inject(ContextService);
      return `/apps/ia-esnc-cartes-onboarding/labels/ia-esnc-cartes-onboarding-${context.language}-${context.typology}.json`;
    }),
    provideDataLayer({
      serviceName: 'porteurCarteBs',
      prefix: 'dataLayer',
      // turn this property to `true` if you want to log warnings in the
      // console if some of your DataLayer objects
      // are formatted properly (only in development mode)
      // it needs the `prefix` property to be set in order to work
      checkDataLayerObjectsIntegrity: false,
    }),
    provideState(globalFeature),
    provideState(enrollmentFeature),
    provideState(reconciliationFeature),
    provideEffects([...globalEffects, ...enrollmentEffects, ...reconciliationEffects]),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      // turn this to `true` if you want to enable 'fondu enchaîné' effect when navigating.
      // withViewTransitions({ skipInitialTransition: true }),
    ),
    provideAppInitializer(() => {
      inject(LabelsService).getTranslation();
      inject(DataLayer);
    }),
    provideHttpClient(withInterceptorsFromDi(), withApimInterceptors()),

    provideJsonConvert(),
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    provideAnimations(),
  ],
};
